import React from 'react';
import {
  List,
  ListItem,
  Typography,
  Popper,
  Fade,
  Paper,
} from '@material-ui/core';
import round from 'lodash/round';
import { Doughnut } from 'react-chartjs-2';
import PropTypes from 'prop-types';

import {
  pieChartWithLegendBesideStyles,
  pieChartWithLegendBelowStyles,
} from '../../css/style';
import ChartSkeleton from './ChartSkeleton';
import LegendColorBox from './LegendColorBox';
import LegendText from './LegendText';

const legendBesideStyles = pieChartWithLegendBesideStyles();
const legendBelowStyles = pieChartWithLegendBelowStyles();
const getLegendStyles = legendPosition => (legendPosition === 'beside' ? legendBesideStyles : legendBelowStyles);

class PieChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLegendPopoverOpen: false,
      anchorEl: null,
    };

    this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
    this.handlePopoverClose = this.handlePopoverClose.bind(this);
  }

  handlePopoverOpen = (event) => {
    this.setState({ anchorEl: event.currentTarget, isLegendPopoverOpen: true });
  }

  handlePopoverClose = () => {
    this.setState({ anchorEl: null, isLegendPopoverOpen: false });
  };

  renderLegendPopover = (totalPoints) => {
    const { isLegendPopoverOpen, anchorEl } = this.state;
    return (
      <Popper
        open={isLegendPopoverOpen}
        anchorEl={anchorEl}
        transition
        placement="left"
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper>
              <PopperLegend
                {...this.props}
                totalPoints={totalPoints}
              />
            </Paper>
          </Fade>
        )}
      </Popper>
    );
  }

  renderLegendAreaWithPopoverResponse = (totalPoints) => {
    const { statsListContainer, hasLegendPopover, legendPosition } = this.props;
    const styles = getLegendStyles(legendPosition);
    return (
      <React.Fragment>
        <div
          style={statsListContainer || styles.statsListContainer}
          onMouseEnter={this.handlePopoverOpen}
          onMouseLeave={this.handlePopoverClose}
        >
          <Legend
            {...this.props}
            totalPoints={totalPoints}
          />
        </div>
        {hasLegendPopover && this.renderLegendPopover(totalPoints)}
      </React.Fragment>
    );
  }


  render() {
    const { data, isLoading, legendPosition } = this.props;
    const styles = getLegendStyles(legendPosition);

    const totalPoints = data.datasets[0].data.length > 0
      ? data.datasets[0].data.reduce((acc, current) => acc + current)
      : 0;

    const chart = (
      <PieCircleWithText
        {...this.props}
        totalPoints={totalPoints}
      />
    );
    const chartOrSkeleton = isLoading ? <PieSkeleton /> : chart;

    const legendArea = this.renderLegendAreaWithPopoverResponse(totalPoints);
    const legendOrSkeleton = isLoading ? <PieLegendSkeleton /> : legendArea;

    return (
      <div style={styles.container}>
        {chartOrSkeleton}
        {legendOrSkeleton}
      </div>
    );
  }
}

const PieCircleWithText = ({
  data,
  legend,
  legendPosition,
  height,
  options,
  totalPoints,
  totalText,
}) => {
  const styles = getLegendStyles(legendPosition);
  return (
    <div style={styles.pieChartAndInnerTextContainer}>
      <Doughnut data={data} legend={legend} height={height} options={options} />
      <div style={styles.innerText}>
        <Typography style={styles.totalPointsText}>
          {totalPoints}
        </Typography>
        {
          totalText && (
            <Typography style={styles.pointsText}>
              {totalText}
            </Typography>
          )
        }
      </div>
    </div>
  );
};

const PieSkeleton = props => (
  <ChartSkeleton
    outerContainerStyle={legendBelowStyles.pieChartSkeletonContainer}
    circle
    height={80}
    width={80}
    count={1}
  />
);

const PieLegendSkeleton = props => (
  <ChartSkeleton
    outerContainerStyle={legendBelowStyles.pieLegendSkeletonContainer}
    count={5}
  />
);

const getPercentageLegendItemText = ({
  data,
  totalPoints,
  label,
  index,
}) => {
  const value = round((data.datasets[0].data[index] / totalPoints) * 100, 1).toFixed(1);
  const valueText = value && !isNaN(value) ? `${value}% \u00A0\u00A0 ` : '';
  const finalText = `${valueText}${label}`;
  return finalText;
};

const getCountLegendItemText = ({
  data,
  index,
  label,
  legendStatSuffix,
}) => {
  const value = data.datasets[0].data[index];
  const finalText = `${label} - ${value} ${legendStatSuffix}`;
  return finalText;
};

const legendStatFunctions = {
  percentage: getPercentageLegendItemText,
  count: getCountLegendItemText,
};

const LegendItems = (props) => {
  const {
    data,
    statsListItem,
    legendPosition,
    totalPoints,
    legendStat,
    legendStatSuffix,
  } = props;
  const styles = getLegendStyles(legendPosition);

  return data.labels.length > 0 && data.labels.map((label, index) => {
    const getLegendTextArgs = {
      data,
      totalPoints,
      label,
      index,
      legendStatSuffix,
    };
    const finalText = legendStatFunctions[legendStat](getLegendTextArgs);
    return (
      <ListItem key={label} style={statsListItem || styles.statsListItem}>
        <LegendItemContent
          legendPosition={legendPosition}
          data={data}
          index={index}
          finalText={finalText}
        />
      </ListItem>
    );
  });
};

const Legend = (props) => {
  const { legendPosition } = props;

  const styles = getLegendStyles(legendPosition);

  return (
    <List style={styles.statsList}>
      <LegendItems {...props} />
    </List>
  );
};

const PopperLegend = (props) => {
  const styles = legendBelowStyles;
  return (
    <div style={styles.popperStatsList}>
      <PopperLegendItems {...props} />
    </div>
  );
};

const PopperLegendItems = (props) => {
  const {
    data,
    legendPosition,
    totalPoints,
    legendStat,
    legendStatSuffix,
  } = props;
  const styles = legendBelowStyles;

  return data.labels.length > 0 && data.labels.map((label, index) => {
    const getLegendTextArgs = {
      data,
      totalPoints,
      label,
      index,
      legendStatSuffix,
    };
    const finalText = legendStatFunctions[legendStat](getLegendTextArgs);
    return (
      <div key={label} style={styles.popperStatsItem}>
        <LegendItemContent
          legendPosition={legendPosition}
          data={data}
          index={index}
          finalText={finalText}
        />
      </div>
    );
  });
};

const LegendItemContent = (props) => {
  const {
    data,
    index,
    finalText,
    legendPosition,
  } = props;
  const styles = getLegendStyles(legendPosition);
  const color = `${data.datasets[0].backgroundColor[index]}`;
  return (
    <React.Fragment>
      <LegendColorBox color={color} />
      <LegendText text={finalText} />
    </React.Fragment>
  );
};

PieChart.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  height: PropTypes.number,
  legend: PropTypes.objectOf(PropTypes.any),
  options: PropTypes.objectOf(PropTypes.any),
  totalText: PropTypes.string,
  legendPosition: PropTypes.string.isRequired,
};

PieChart.defaultProps = {
  height: 240,
  legend: null,
  options: null,
  totalText: '',
};

export default PieChart;
